import React, { type PropsWithChildren, useCallback, useMemo } from 'react';

import UFOSegment from '@atlaskit/react-ufo/segment';
import {
	STATSIG_SEARCH_PLATFORM_JIRA_LAYER,
	STATSIG_SEARCH_PLATFORM_JIRA_LAYER_TENANTS_BASED,
	useExperimentConfig,
} from '@atlassian/search-experiment';

import type { BootstrapState, Features } from '../../../../common/constants/bootstrap';
import { type NounKeys } from '../../../../common/constants/nouns';
import {
	allProductKeys,
	type PrimaryProductKey,
	PrimaryProductKeys,
	ProductKeys,
} from '../../../../common/constants/products';
import { type SearchQueryStringParamsRaw } from '../../../../common/constants/schemas/query-params';
import { SearchExperience, type SearchStoreExperience } from '../../../../common/constants/search';
import { type UserDetails } from '../../../../common/constants/types';
import { getAccessibleProducts } from '../../../../common/utils/bootstrap';
import { getMultiSiteSearchInRovo } from '../../../../common/utils/experiments';
import { useFetchSmartLinkUsage } from '../../../3p-smart-link-usage';
import { UpdateBulkConnectLocalStorage } from '../../../bulk-connect/update-bulk-connect-local-storage';
import { useFetchConfig, type UseFetchConfigProps } from '../../../config';
import { useLogException } from '../../../error-boundary/utils';
import { BootstrapContextProvider } from '../bootstrap-context-provider';
import { BootstrapStoreContainer } from '../index';
import { serveBootstrapProviderExp } from '../utils';

export type BootstrapContainerProps = PropsWithChildren<
	{
		/**
		 * Passing this will override the behaviour of bootstrapping in all product mode.
		 * The UI will only show the products which have been passed PROVIDED the user has access to them.
		 *
		 * NOTE: QS-4325: The `products` parameter is deprecated as we are removing it as a
		 * prop from the root <SearchPage/>.
		 */
		products?: ProductKeys[];

		nouns?: NounKeys[];

		/**
		 * The product the search page will be rendered in.
		 */
		primaryProduct: PrimaryProductKey;

		/**
		 * This restricts the searches only to the cloudId that was passedin.
		 * This is a Jira/Confluence term and unfortunately most of the Atlassian products continue to be tied to a site.
		 *
		 * This propertly is useless for Trello or Bitbucket which are Workspace scoped.
		 *
		 * Not passing this property would bootstrap the UI in the site on which the user is most active
		 */
		cloudId?: string;

		/**
		 * The absolute url to the Atlassian Graphql Gateway. If not passed in the relative URL would be used.
		 */
		aggAbsoluteUrl?: string;

		/**
		 * Details of the current user.
		 */
		user?: UserDetails;

		/**
		 * Extra attributes to be attached to analytics events that are fired from within Full Page Search
		 */
		analyticsAttributes?: Record<string, any>;

		SAINHostName?: string;

		orgId?: string;

		queryParams?: Record<string, any>;

		/**
		 * Features that are enabled/disabled for the search page
		 */
		features?: Features;

		/**
		 * A unique identifier for the search session. If not passed, a random UUID will be generated.
		 */
		searchSessionId?: string | null;

		callbacks?: {
			/**
			 * A callback that is invoked when the query parameters in the URL need to be updated.
			 * The consuming product can implement the specific steps to be done when this callback is invoked.
			 *
			 * @param queryParamObject An object which is key:value map of things that should be set as the query parameter.
			 */
			updateQueryParameters?: (queryParamObject: Partial<SearchQueryStringParamsRaw>) => void;

			/**
			 * @deprecated this prop is deprecated and not used anymore. It will be removed soon.
			 *
			 * @returns the space IDs for the given space keys. NOTE: these should be the space IDs, not ARIs.
			 * @example getSpaceIdsFromSpaceKeys(["STATUS"]); // Promise<["123454321"]>
			 **/
			getSpaceIdsFromSpaceKeys?: (spaceKeys: string[]) => Promise<string[]>;

			/**
			 * A callback that is invoked when the Quick Find search completes successfully.
			 */
			onSearchCompleted?: (resultCount?: number, query?: string) => void;
		};

		/**
		 * The Statsig layer used to run mutually exclusive frontend and backend experiments
		 */
		statsigLayer?: string;
		/**
		 * Each product is responsible for providing the list of backend search experiment layers.
		 * Each string in the list is a stringified JSON object that represents a layer and
		 * the set of criteria for a search request to match on and be enrolled in a given backend experiment.
		 * This is defined via Statsig dynamic config: https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/dynamic_configs/search_platform_layer_functions
		 */
		experimentLayers?: string[];

		isSSR?: boolean;

		/**
		 * The search experience for which the bootstrap store is being provided. This is important to pass in as a prop as it may be utilised e.g. in useFetchConfig before the stores are initialised.
		 *
		 * Optional because from https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/226846/overview this is now exported for Rovo Home, we'll need to upgrade that before making this prop required. Defaults to SearchExperience.FULL_PAGE_SEARCH
		 */
		experience?: SearchStoreExperience;

		/**
		 * This determines if board entities can be fetched or not
		 */
		hasEditOrViewPlanPermission?: boolean;
	} & Pick<
		UseFetchConfigProps,
		'configFetchAbsoluteUrl' | 'thirdPartyAbsoluteUrl' | 'thirdPartyDisabled' | 'aggregatorHostName'
	>
>;

export const BootstrapStoreProvider = ({
	children,
	experience = SearchExperience.SEARCH_PAGE,
	...props
}: PropsWithChildren<BootstrapContainerProps>): React.JSX.Element => {
	const {
		cloudId,
		configFetchAbsoluteUrl,
		user,
		thirdPartyAbsoluteUrl,
		primaryProduct,
		aggAbsoluteUrl,
		SAINHostName,
		aggregatorHostName,
		orgId,
		analyticsAttributes,
		products,
		queryParams,
		features,
		searchSessionId,
		callbacks,
		experimentLayers,
		hasEditOrViewPlanPermission,
		isSSR,
	} = props;

	const onError = useLogException();
	const bootstrapProviderExp = serveBootstrapProviderExp();
	const smartLinkUsage = useFetchSmartLinkUsage(experience, cloudId);

	const combinedExperimentLayer = useMemo(() => {
		// When Quick Find mount from Jira. It's eligible to run the Jira layer experiments.
		return primaryProduct === PrimaryProductKeys.Jira
			? [STATSIG_SEARCH_PLATFORM_JIRA_LAYER_TENANTS_BASED, STATSIG_SEARCH_PLATFORM_JIRA_LAYER]
			: undefined;
	}, [primaryProduct]);

	const experimentConfig = useExperimentConfig({
		// When Quick Find mount from Jira. It's eligible to run the Jira layer experiments.
		combinedExperimentLayer,
		experimentLayers: experimentLayers ?? [],
		sideBySideExperiment: features?.sideBySide?.experiments,
		// TODO: QS-9370: Remove unused statsigLayer param
		statsigLayer: undefined,
		onError,
	});

	const multiSiteSearchInRovo = getMultiSiteSearchInRovo(experimentConfig);

	const {
		accessibleProducts,
		accessibleNouns,
		thirdPartyConfigs,
		firstCloudId,
		intercomHmac,
		isNounsAvailable,
		isRovoEnabled,
		isJiraIssueIndexed,
		productEdition,
		productDataRegions,
		rovoFeatures,
		isConfigLoaded,
		linkedCloudARIs,
	} = useFetchConfig({
		cloudId,
		configFetchAbsoluteUrl,
		aggregatorHostName,
		userId: user?.id,
		thirdPartyAbsoluteUrl,
		primaryProduct,
		aggAbsoluteUrl,
		multiSiteSearchInRovo,
		experience,
		activationId: analyticsAttributes?.activationId,
	});

	const allFilterExcludedProducts = useMemo(
		() => [ProductKeys.Trello, ProductKeys.Gmail, ProductKeys.OutlookMail],
		[],
	);

	const getInitialProps = useCallback(() => {
		let storeContainerInitialProps: Partial<BootstrapState> = {
			cloudId: cloudId ?? firstCloudId,
			aggAbsoluteUrl,
			SAINHostName,
			aggregatorHostName,
			user,
			primaryProduct,
			orgId,
			analyticsAttributes,
			features,
			experimentConfig,
			experimentLayers,
			intercomHmac,
			isNounsAvailable,
			isRovoEnabled,
			isJiraIssueIndexed,
			isConfigLoaded,
			products: getAccessibleProducts({
				supportedProducts: allProductKeys,
				products,
				accessibleProducts,
			}),
			nouns: accessibleNouns,
			allFilterExcludedProducts,
			productEdition,
			productDataRegions,
			rovoFeatures,
			linkedCloudARIs,
			hasEditOrViewPlanPermission,
			smartLinkUsage,
		};

		if (searchSessionId) {
			storeContainerInitialProps.searchSessionId = searchSessionId;
		}
		if (accessibleProducts) {
			storeContainerInitialProps.thirdPartyConfigs = thirdPartyConfigs;
		}

		return storeContainerInitialProps;
	}, [
		cloudId,
		firstCloudId,
		aggAbsoluteUrl,
		SAINHostName,
		aggregatorHostName,
		user,
		primaryProduct,
		orgId,
		analyticsAttributes,
		features,
		experimentConfig,
		intercomHmac,
		isNounsAvailable,
		isRovoEnabled,
		isJiraIssueIndexed,
		isConfigLoaded,
		products,
		accessibleProducts,
		accessibleNouns,
		allFilterExcludedProducts,
		productEdition,
		productDataRegions,
		searchSessionId,
		thirdPartyConfigs,
		rovoFeatures,
		linkedCloudARIs,
		experimentLayers,
		hasEditOrViewPlanPermission,
		smartLinkUsage,
	]);

		if (bootstrapProviderExp) {
			const initialProps = getInitialProps();
			return (
				<UFOSegment name="bootstrap-context">
					<BootstrapContextProvider
						initialProps={initialProps}
						queryParams={queryParams}
						queryParamsCallback={callbacks?.updateQueryParameters}
						getSpaceIdsFromSpaceKeys={callbacks?.getSpaceIdsFromSpaceKeys}
						onSearchCompleted={callbacks?.onSearchCompleted}
						experience={experience}
					>
						{children}
					</BootstrapContextProvider>
					{!isSSR && (
						<UpdateBulkConnectLocalStorage
							accessibleProducts={accessibleProducts}
							isConfigLoaded={isConfigLoaded}
						/>
					)}
				</UFOSegment>
			);
		} else {
			return (
				<UFOSegment name="bootstrap-rss">
					<BootstrapStoreContainer
						initialProps={getInitialProps()}
						queryParams={queryParams}
						queryParamsCallback={callbacks?.updateQueryParameters}
						getSpaceIdsFromSpaceKeys={callbacks?.getSpaceIdsFromSpaceKeys}
						onSearchCompleted={callbacks?.onSearchCompleted}
					>
						{children}
					</BootstrapStoreContainer>
					{!isSSR && (
						<UpdateBulkConnectLocalStorage
							accessibleProducts={accessibleProducts}
							isConfigLoaded={isConfigLoaded}
						/>
					)}
				</UFOSegment>
			);
		}
};
